import { useEffect } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useAtomValue } from "jotai";
import { Input } from "@common/Form/Input";
import { Button } from "@common/Button";
import { ComponentColor, ComponentSize, LabelPlacement } from "@common/types";
import { useLegalClassRestrictionApi } from "@/hooks/apiHooks/useLegalClassRestrictionApi";
import { isProcessingLegalClassRestrictionsAtom } from "@/store/legalClassRestrictionAtoms";
import type {
    LegalClassRestriction,
    LegalClassRestrictionRequest,
} from "@models/legalClassRestriction";
import { LEGAL_CLASS_OPTIONS } from "@models/product";
import type { LegalClassType } from "@models/product";
import { US_STATES } from "@utils/usStates";
import { toDateInputValue } from "../_helpers/restrictionStatus";

const DIALOG_ID = "legalClassRestrictionModal";

// Located by id rather than a ref, matching EditCategory: the submit handler
// runs through handleSubmit during render, and a ref read from it trips
// react-hooks/refs.
const dialogElement = (): HTMLDialogElement | null => {
    const element = document.getElementById(DIALOG_ID);
    return element instanceof HTMLDialogElement ? element : null;
};

// Mirrors CreateLegalClassRestrictionValidator / UpdateLegalClassRestrictionValidator
// so the admin is not taught the rules by a 400.
//
// Two of the server's rules are enforced structurally rather than by a message,
// because a form that cannot express the violation is a better mirror than one
// that reports it:
//   - `legalClass != None` — LegalClassType has no None member and the picker
//     only lists the five real classes, so from a NEW rule only "nothing
//     chosen" is reachable. An EXISTING row can carry a value the picker does
//     not list (a legacy None, a class added server-side); see the unmapped
//     handling in the component.
//   - `^[A-Za-z]{2}\z` — the state control is a picker of USPS codes. The
//     server accepts any two letters, so a stored PR / GU / VI row is likewise
//     a value the picker does not list.
// The XOR between state and zipCode is the same idea: `scope` chooses which one
// exists, so "both" cannot be expressed and only "neither" needs a message.
const restrictionSchema = z
    .object({
        legalClass: z.string().nonempty("Choose a legal class"),
        scope: z.enum(["state", "zipCode"]),
        state: z.string(),
        zipCode: z.string(),
        effect: z.enum(["restricted", "allowed"]),
        startDate: z.string().nonempty("Start date is required"),
        endDate: z.string(),
    })
    .superRefine((data, ctx) => {
        if (data.scope === "state" && !data.state) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["state"],
                message: "Choose a state",
            });
        }

        if (data.scope === "zipCode" && !/^\d{5}$/.test(data.zipCode)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["zipCode"],
                message: "ZIP code must be five digits",
            });
        }

        // Both are yyyy-MM-dd, so a string compare is a date compare.
        if (data.endDate && data.startDate && data.endDate <= data.startDate) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["endDate"],
                message: "End date must be after the start date",
            });
        }
    });

type RestrictionFormData = z.infer<typeof restrictionSchema>;

// yyyy-MM-dd → the ISO datetime the API's DateTime binder takes. Sent without a
// timezone marker deliberately: the value the admin typed is the date the rule
// takes effect, and appending "Z" (or letting a Date round-trip apply the
// browser's offset) would move a compliance date by a day for most of the US.
const toApiDate = (value: string): string => `${value}T00:00:00`;

interface RestrictionFormDialogProps {
    /** The rule being edited, or null to create a new one. */
    restriction: LegalClassRestriction | null;
    /** Called after the dialog's native close steps have run. */
    onClose: () => void;
    /** Called on a successful save, before the dialog closes. */
    onSaved: () => void;
}

export const RestrictionFormDialog = ({
    restriction,
    onClose,
    onSaved,
}: RestrictionFormDialogProps) => {
    const { createLegalClassRestriction, updateLegalClassRestriction } =
        useLegalClassRestrictionApi();
    const isProcessing = useAtomValue(isProcessingLegalClassRestrictionsAtom);

    // A stored value the pickers cannot show. RHF would keep it while the
    // control read "Choose…", so Save would PUT the very value the schema
    // exists to block — or, once the admin "fixed" the blank by picking
    // something, silently retarget a live compliance rule that was never named
    // on screen. So the value is rendered as its own, named row, and Save is
    // refused while it is still selected; changing it is then an explicit act.
    const storedLegalClass = restriction ? String(restriction.legalClass) : "";
    const unmappedLegalClass =
        storedLegalClass &&
        !LEGAL_CLASS_OPTIONS.some((option) => option.value === storedLegalClass)
            ? storedLegalClass
            : null;
    // Uppercased to match the picker's USPS codes. The stored value is only
    // constrained to two letters of EITHER case — the server uppercases at
    // evaluation time, not on write — so a row holding "ca" must still find
    // California.
    const storedState = restriction?.state?.toUpperCase() ?? "";
    const unmappedState =
        storedState && !US_STATES.some((usState) => usState.code === storedState)
            ? storedState
            : null;

    const methods = useForm<RestrictionFormData>({
        resolver: zodResolver(restrictionSchema),
        defaultValues: {
            legalClass: storedLegalClass,
            scope: restriction?.zipCode ? "zipCode" : "state",
            state: storedState,
            zipCode: restriction?.zipCode ?? "",
            // Restricted is the default because it is the only effect that
            // does anything: the server's evaluation reads !IsLegal rows only.
            effect: restriction?.isLegal ? "allowed" : "restricted",
            startDate: toDateInputValue(restriction?.startDate ?? null),
            endDate: toDateInputValue(restriction?.endDate ?? null),
        },
    });
    // `scope` picks which destination field exists; `effect` drives the
    // allow-rule caveat. react-hook-form's watch() cannot be memoized, so the
    // React Compiler skips this component — informational, and suppressed at
    // the call site the same way Common/Table.tsx handles TanStack Table.
    // eslint-disable-next-line react-hooks/incompatible-library
    const [scope, effect] = methods.watch(["scope", "effect"]);
    const errors = methods.formState.errors;

    useEffect(() => {
        dialogElement()?.showModal();
    }, []);

    const onSubmit = async (data: RestrictionFormData) => {
        // Never save an unmapped value: the message names it, so the admin
        // knows what the rule targets today and what picking a class changes.
        if (unmappedLegalClass !== null && data.legalClass === unmappedLegalClass) {
            methods.setError("legalClass", {
                message: `Legal class ${unmappedLegalClass} isn't one this form can save. Choose a class to change what this rule applies to.`,
            });
            return;
        }
        if (
            data.scope === "state" &&
            unmappedState !== null &&
            data.state === unmappedState
        ) {
            methods.setError("state", {
                message: `State ${unmappedState} isn't in this list. Choose a state to change where this rule applies.`,
            });
            return;
        }

        const body: LegalClassRestrictionRequest = {
            legalClass: Number(data.legalClass) as LegalClassType,
            state: data.scope === "state" ? data.state : null,
            zipCode: data.scope === "zipCode" ? data.zipCode : null,
            isLegal: data.effect === "allowed",
            startDate: toApiDate(data.startDate),
            endDate: data.endDate ? toApiDate(data.endDate) : null,
        };

        try {
            if (restriction) {
                await updateLegalClassRestriction(restriction.id, body);
            } else {
                await createLegalClassRestriction(body);
            }

            onSaved();
            dialogElement()?.close();
        } catch {
            // useApi already toasted; keep the dialog open with the values the
            // admin typed so the save can be retried or corrected.
        }
    };

    const selectClass =
        "select select-bordered select-primary w-full bg-white text-neutral";
    const errorClass = "text-red-500 text-sm mt-1";

    return (
        <dialog
            id={DIALOG_ID}
            aria-labelledby={`${DIALOG_ID}-title`}
            className="modal text-neutral"
            onCancel={(e) => {
                if (isProcessing) {
                    e.preventDefault();
                }
            }}
            onClose={onClose}
        >
            <div className="modal-box max-w-2xl">
                <h1
                    id={`${DIALOG_ID}-title`}
                    className="font-bold uppercase text-3xl"
                >
                    {restriction ? "Edit Restriction" : "New Restriction"}
                </h1>
                <div className="divider divider-info mt-0"></div>
                <FormProvider {...methods}>
                    <form onSubmit={methods.handleSubmit(onSubmit)}>
                        <div className="space-y-4">
                            <div>
                                <span className="font-semibold block mb-1">
                                    Legal class
                                </span>
                                <select
                                    className={selectClass}
                                    aria-label="Legal class"
                                    {...methods.register("legalClass")}
                                >
                                    <option value="">Choose a class...</option>
                                    {unmappedLegalClass !== null && (
                                        <option value={unmappedLegalClass}>
                                            Unknown value: {unmappedLegalClass}
                                        </option>
                                    )}
                                    {LEGAL_CLASS_OPTIONS.map((option) => (
                                        <option
                                            key={option.value}
                                            value={option.value}
                                        >
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                                {errors.legalClass && (
                                    <p className={errorClass}>
                                        {errors.legalClass.message}
                                    </p>
                                )}
                            </div>

                            <div>
                                <span className="font-semibold block mb-1">
                                    Applies to
                                </span>
                                <select
                                    className={selectClass}
                                    aria-label="Applies to"
                                    {...methods.register("scope")}
                                >
                                    <option value="state">
                                        A whole state
                                    </option>
                                    <option value="zipCode">
                                        A single ZIP code
                                    </option>
                                </select>
                            </div>

                            {scope === "state" ? (
                                <div>
                                    <span className="font-semibold block mb-1">
                                        State
                                    </span>
                                    <select
                                        className={selectClass}
                                        aria-label="State"
                                        {...methods.register("state")}
                                    >
                                        <option value="">
                                            Choose a state...
                                        </option>
                                        {unmappedState !== null && (
                                            <option value={unmappedState}>
                                                Unknown value: {unmappedState}
                                            </option>
                                        )}
                                        {US_STATES.map((usState) => (
                                            <option
                                                key={usState.code}
                                                value={usState.code}
                                            >
                                                {usState.name}
                                            </option>
                                        ))}
                                    </select>
                                    {errors.state && (
                                        <p className={errorClass}>
                                            {errors.state.message}
                                        </p>
                                    )}
                                </div>
                            ) : (
                                <Input
                                    name="zipCode"
                                    placeholder="12345"
                                    size={ComponentSize.SM}
                                    labelPlacement={LabelPlacement.Inside}
                                    labelText="ZIP code"
                                    fullWidth={true}
                                    inputClass="text-black"
                                    inputMode="numeric"
                                />
                            )}

                            <div>
                                <span className="font-semibold block mb-1">
                                    Effect
                                </span>
                                <select
                                    className={selectClass}
                                    aria-label="Effect"
                                    {...methods.register("effect")}
                                >
                                    <option value="restricted">
                                        Restricted — block this class here
                                    </option>
                                    <option value="allowed">
                                        Allowed — record this class as legal
                                        here
                                    </option>
                                </select>
                                {effect === "allowed" && (
                                    <p className="text-xs text-gray-500 mt-1">
                                        An allow rule is a record only. It does
                                        not block anything and cannot override a
                                        restriction.
                                    </p>
                                )}
                            </div>

                            <Input
                                name="startDate"
                                type="date"
                                size={ComponentSize.SM}
                                labelPlacement={LabelPlacement.Inside}
                                labelText="Start date"
                                fullWidth={true}
                                inputClass="text-black"
                            />
                            <Input
                                name="endDate"
                                type="date"
                                size={ComponentSize.SM}
                                labelPlacement={LabelPlacement.Inside}
                                labelText="End date (excluded)"
                                fullWidth={true}
                                inputClass="text-black"
                            />
                            {/*
                              * The stored window is half-open and is NOT
                              * converted on write — the API and every other
                              * client read back exactly the date typed here —
                              * so the exclusivity has to be said out loud.
                              * Reading "Ends" as the last day covered is the
                              * natural mistake, and it un-blocks a restricted
                              * class a full day early.
                              */}
                            <p className="text-xs text-gray-500">
                                Dates are UTC and the end date is excluded: the
                                rule is in force from the start date up to, but
                                not on, the end date. To keep a restriction in
                                force through 30 September, enter 1 October.
                            </p>
                            <p className="text-xs text-gray-500">
                                Leave the end date blank for an open-ended rule.
                                Setting it is how a repealed restriction is
                                retired — the row stays as a record of when it
                                was in force.
                            </p>
                        </div>
                        <div className="modal-action">
                            <Button
                                color={ComponentColor.Primary}
                                type="submit"
                                loading={isProcessing}
                            >
                                {isProcessing ? "Saving..." : "Save"}
                            </Button>
                            <Button
                                color={ComponentColor.Neutral}
                                loading={isProcessing}
                                onClick={() => dialogElement()?.close()}
                            >
                                Cancel
                            </Button>
                        </div>
                    </form>
                </FormProvider>
            </div>
            {!isProcessing && (
                // method="dialog" closes natively; the close event fires onClose.
                <form method="dialog" className="modal-backdrop">
                    <button>close</button>
                </form>
            )}
        </dialog>
    );
};
